import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, SITE_OG_LOCALE, getDefaultOgImageUrl, toCanonicalUrl } from '../config/site';

/**
 * Global SEO head: title, description, canonical, Open Graph, Twitter Card.
 *
 * @param {string} title
 * @param {string} description
 * @param {string} path - Pathname only, e.g. `/`, `/blog`, `/blog/my-post`
 * @param {'website'|'article'} [ogType]
 * @param {string} [ogImage] - Full URL; defaults to site OG image
 * @param {string} [ogImageAlt]
 * @param {'summary_large_image'|'summary'} [twitterCard]
 * @param {string} [articlePublishedTime] - ISO date for articles
 * @param {string} [articleModifiedTime] - ISO date
 * @param {boolean} [noindex]
 */
export default function SeoMeta({
  title,
  description,
  path = '/',
  ogType = 'website',
  ogImage,
  ogImageAlt,
  twitterCard = 'summary_large_image',
  articlePublishedTime,
  articleModifiedTime,
  noindex = false,
  children,
}) {
  const canonicalUrl = toCanonicalUrl(path);
  const imageUrl = ogImage || getDefaultOgImageUrl();
  const imageAlt = ogImageAlt || `${SITE_NAME} — ${title}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={SITE_OG_LOCALE} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={imageAlt} />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {ogType === 'article' && articlePublishedTime ? (
        <meta property="article:published_time" content={articlePublishedTime} />
      ) : null}
      {ogType === 'article' && articleModifiedTime ? (
        <meta property="article:modified_time" content={articleModifiedTime} />
      ) : null}
      {ogType === 'article' ? <meta property="article:author" content={SITE_NAME} /> : null}

      {children}
    </Helmet>
  );
}
